import React, { useRef } from 'react';
import moment from 'moment';
import {
  Chart as ChartJS,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  Title,
  TimeScale,
  TimeSeriesScale,
} from 'chart.js';
import { getElementAtEvent, Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'chartjs-adapter-moment';
import { isMobile } from 'react-device-detect';

import { colors } from 'global/colors';
import { getTimeOfRun } from 'functions/reportFunctions';
import { GraphContainerSmall } from './styles';

ChartJS.register(PointElement, LineElement, ChartDataLabels, Title, Tooltip, Legend, TimeScale, TimeSeriesScale);

export default React.memo(({ ticket, inputs, setCurrentInput, selectedInput, pastedTextsInputs, showFullOverTime }) => {
  const chartRef = useRef(null);
  const MAX_INTERVAL = 20;

  const hasOverTime = inputs.some((input) => input.isBestAfterDeadline);
  let lastInput = inputs.find((input) => input.isFinalSubmit && !input.isBestAfterDeadline);

  // we calculate the differenceInterval based on normal time in the score graph, so we need to do the same here
  const differenceInterval =
    (new Date(lastInput.timeOfRequest || lastInput.timeOfPaste || lastInput.updatedAt) - new Date(ticket.startedAt)) /
    1000 /
    MAX_INTERVAL;

  // find the input that has the latest timeOfRequest or timeOfPaste
  if (showFullOverTime)
    lastInput = inputs.sort((a, b) => {
      return (
        new Date(a.timeOfRequest ?? a.timeOfPaste ?? a.updatedAt) -
        new Date(b.timeOfRequest ?? b.timeOfPaste ?? b.updatedAt)
      );
    })[inputs.length - 1];

  let lastTime = new Date(lastInput.timeOfRequest || lastInput.timeOfPaste || lastInput.updatedAt);

  // If we dont show fullOvertime we remove the paste events that are made in overtime
  if (!showFullOverTime) {
    pastedTextsInputs = pastedTextsInputs.filter((input) => new Date(input.timeOfPaste) < lastTime);
  }

  // makes it easier to debug
  let hideAxisAndTicks = true;

  const hasOneInput = pastedTextsInputs?.length === 1;
  let selectedRunIndex = -1;
  let timeLabels = hasOneInput ? [new Date(ticket.startedAt)] : [];
  let timeRunArray = hasOneInput ? ['0:00'] : [];
  let beforeDeadlineDataset = [];
  // adjustmentDataSet it used to add dots so the x-axis will be the same as the score graph.
  let adjustmentDataSet = [];
  let isLessDataForTimeAxis =
    hasOneInput &&
    new Date(
      pastedTextsInputs[0]?.timeOfRequest || pastedTextsInputs[0]?.timeOfPaste || pastedTextsInputs[0]?.updatedAt
    ) -
      new Date(ticket.startedAt) <
      30000;

  pastedTextsInputs.forEach((input, index) => {
    if (input._id === selectedInput._id) selectedRunIndex = index;
    let inputDate = new Date(input.timeOfRequest || input.timeOfPaste || input.updatedAt);

    timeLabels.push(inputDate);
    timeRunArray.push(getTimeOfRun(input, ticket));

    // if we only have one input we need to add a empty dot, otherwise it will not render
    if (hasOneInput) beforeDeadlineDataset.push({ x: undefined, y: undefined });

    beforeDeadlineDataset.push({ x: inputDate, y: 100 });
    adjustmentDataSet.push({ x: undefined, y: undefined });
  });

  let date = new Date(lastTime);
  // only add the difference if we have to adjust for a overtime dot
  date.setSeconds(date.getSeconds() + (hasOverTime && !showFullOverTime ? differenceInterval : 0));
  timeRunArray.push(getTimeOfRun(lastInput, ticket));
  adjustmentDataSet.push({ x: date, y: 90 });

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        usePointStyle: true,
        caretPadding: 10,
        filter: (tooltipItem) => (hasOneInput && tooltipItem?.dataIndex === 0 ? false : true),
        callbacks: {
          labelPointStyle: () => ({ pointStyle: 'circle' }),
          labelColor: () => ({ backgroundColor: colors.fsDarkBlue }),
          title: (tooltipItems) => {
            if (!tooltipItems?.length) return;
            let tooltip = `Time: ${timeRunArray[tooltipItems[0].dataIndex]}`;
            return tooltip;
          },
          label: (context) => ``,
        },
      },
      datalabels: {
        color: colors.darkGray,
        align: 'top',
        // rotation: rotateLabels ? 340 : 0,
        // rotation: 0,
        labels: {
          title: {
            font: {
              weight: 'bold',
            },
          },
        },
        display: true,
        formatter: (_, context) => {
          return '';
        },
      },
    },
    scales: {
      x: {
        border: { color: hideAxisAndTicks ? 'white' : '' },
        label: {
          color: colors.fsDarkBlue,
        },
        grid: {
          display: false,
        },
        title: {
          display: false,
          text: 'Time (mm:ss)',
          color: 'white',
          font: { weight: 'bold' },
        },
        type: 'time', // Set type to 'timeseries' if we want points to be equidistant
        suggestedMin: ticket.startedAt,
        ticks: {
          color: hideAxisAndTicks ? 'white' : '',
          maxTicksLimit: MAX_INTERVAL,
          // Needs to have the same rotation as the score graph, otherwise it will not align
          maxRotation: 27,
          minRotation: 27,
          font: { size: isMobile ? 8 : 12 },
          callback: (value, a, b) => {
            if (isLessDataForTimeAxis && a !== b.length - 1) return;
            let isOverDeadline = new Date(lastTime) < new Date(value);
            let time = moment(value) - moment(ticket.startedAt);
            let minutes = Math.floor(time / 60000);
            let seconds = ((time % 60000) / 1000).toFixed(0);
            return isOverDeadline
              ? ''
              : seconds === 60
                ? minutes + 1 + ':00'
                : minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
          },
        },
      },
      y: {
        border: { color: hideAxisAndTicks ? 'white' : '' },
        suggestedMin: 90,
        suggestedMax: 100,
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: 'Paste',
          position: 'top',
          font: { weight: 'bold', size: 13 },
        },
        ticks: {
          display: true,
          color: hideAxisAndTicks ? 'white' : '',
          font: { size: isMobile ? 8 : 12 },
        },
      },
    },
    layout: {
      padding: {
        right: 30,
      },
    },
    elements: {
      point: {
        radius: (ctx) => (hasOneInput && ctx.dataIndex === 0 ? 0 : 5),
        hoverRadius: (ctx) => (hasOneInput && ctx.dataIndex === 0 ? 0 : 8),
        hoverBackgroundColor: colors.darkGray3,
        pointHoverBorderColor: colors.darkGray3,
        hoverCursor: 'pointer',
      },
    },
    onHover: (_, el) => {
      let canvas = document.getElementById('pasteEventTimelineGraph');
      canvas.style.cursor = el[0] ? 'pointer' : 'default';
    },
  };

  const pointColors = timeLabels.map((_, index) => (index === selectedRunIndex ? colors.darkGray3 : colors.lightBlue6));

  const data = {
    datasets: [
      {
        data: beforeDeadlineDataset,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointColors,
        segment: {
          borderColor: 'white',
        },
      },
      {
        data: adjustmentDataSet,
        pointBackgroundColor: ['white'],
        pointBorderColor: ['white'],
        segment: {
          borderColor: (ctx) => (ctx.p0DataIndex < selectedRunIndex ? colors.darkGray3 : colors.yellowTransparent),
        },
      },
    ],
  };

  const onClick = (event) => {
    const { current: chart } = chartRef;
    if (!chart) return;
    let element = getElementAtEvent(chart, event);
    if (!element.length) return;
    const { index } = element[0];
    if (hasOneInput && index === 0) return;
    setCurrentInput(hasOneInput ? pastedTextsInputs[0] : pastedTextsInputs[index]);
  };

  return (
    <GraphContainerSmall>
      <Line id="pasteEventTimelineGraph" ref={chartRef} options={options} data={data} onClick={onClick} />
    </GraphContainerSmall>
  );
});
